/* @flow */
import Store from 'lib/Store';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import ChatMemberStore from 'chat/stores/ChatMemberStore';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import Chats from 'collections/Chats';
import Group from 'entities/Group';
import GroupStore from 'groups/stores/GroupStore';
import Message from 'entities/Message';
import PotentialChatMember from 'entities/PotentialChatMember';
import PotentialChatMembers from 'collections/PotentialChatMembers';
import { ActionTypes as AppActions } from 'constants/AppConstants';
import { ActionTypes as GroupActionTypes } from 'groups/constants/GroupConstants';
import { ActionTypes as LeadActions } from 'constants/LeadConstants';
import { ActionTypes as RelationshipActionTypes } from 'constants/RelationshipConstants';
import { ActionTypes, CHATS_PAGE_SIZE } from 'chat/constants/ChatConstants';
import { ChatMessage, chatMessageFactory, ChatGapMessage, shouldListenToPushEventFrom } from 'entities/ChatMessage';
import { PushEvents } from 'pusher/PusherConstants';
import { rootGroup } from 'entities/RootGroup';

import type { MessageType } from 'chat/lib/MessageInterfacePropType';

class ChatStore extends Store {
  // TODO improve some of the filtering logic https://github.com/remind101/r101-frontend/pull/2327#issuecomment-200454662
  getInitialState() {
    return {
      chats: new Chats(),
      currentId: null,
      localChatInstance: null,
      members: new PotentialChatMembers(),
      invalidated: false,
      allMembersLoaded: false,
      focusView: null,
      // Includes chat list and search results
      chatsAndSearchedChats: new Chats(),
      loadedGroups: {}, // whether this group was specifically fetched
      allChatsLoadedForGroups: {},
    };
  }

  getChat(chatId: string): ?Chat {
    return this.state.chats.get(chatId) || this.state.chatsAndSearchedChats.get(chatId);
  }

  getChats(group: Group): Chats {
    return this.state.chats.selectChatsInGroup(group);
  }

  getChatsAndSearchedChats(group: Group): Chats {
    return this.state.chatsAndSearchedChats.selectChatsInGroup(group);
  }

  getCurrentChat(): ?Chat {
    return this.state.currentId ? this.getChat(this.state.currentId) : null;
  }

  getMembers(group = { id: rootGroup.id }): PotentialChatMembers {
    const { members } = this.state;
    if (group.id === rootGroup.id) {
      return this.state.members;
    } else {
      // TODO filter by PCMs who are in the group
      const memberModels = members.filter(m => m.isMemberOfGroup(group.id));
      return new PotentialChatMembers(memberModels);
    }
  }

  getEligibleMemberModels(): Array<PotentialChatMember> {
    if (!this.state.eligibleMembers) {
      this.state.eligibleMembers = this.state.members.filter(member => member.isEligible());
    }
    return this.state.eligibleMembers;
  }

  getFocusView() {
    return this.state.focusView;
  }

  areAllChatsLoaded(): boolean {
    return !!this.state.allChatsLoadedForGroups[rootGroup.id];
  }

  // Have we processed yet a response for a request for chats of this particular group
  haveChatsBeenLoadedForGroup(group: Group): boolean {
    return !!this.state.loadedGroups[group.id];
  }

  areAllChatsLoadedForGroup(group: Group): boolean {
    return this.areAllChatsLoaded() || !!this.state.allChatsLoadedForGroups[group.id];
  }

  areAllMembersLoaded(): boolean {
    return this.state.allMembersLoaded;
  }

  setAsRead(chatId): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.markAsRead();
    }
  }

  setPosition(chat): void {
    let position = 0;
    const firstChat = this.state.chats.first();
    if (!chat.isNew() && firstChat && firstChat.isNew()) {
      position = 1;
    }

    this.state.chats.setPosition(chat, position);
  }

  setLastMessageForChat(chatId: string, message: MessageType): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.setLastMessage(message);

      const updatedAt = chat.getUpdatedAt();
      if (updatedAt < message.getCreatedAt()) {
        chat.setUpdatedAt(message.getCreatedAt());
        this.state.chats.sort();
      }
    }
  }

  setFocusView(focus) {
    this.state.focusView = focus;
  }

  deactivate(chatId): void {
    const chat = this.state.chats.get(chatId);
    if (chat) {
      chat.deactivate();
    }
  }

  handleIncomingChatMessage(message: ChatMessage) {
    const chatId = message.getChatId();

    const chat = this.state.chats.get(chatId);

    if (chat) {
      this.setLastMessageForChat(chatId, message);
      this.emitChange();
    } else {
      ChatActions.loadChat(chatId);
    }
  }

  clearLocalChat() {
    this.state.chats.remove(this.state.localChatInstance);
    this.state.localChatInstance = null;
  }

  loadLocalChat(members, title, originIds) {
    this.clearLocalChat();
    this.state.localChatInstance = Chat.createLocal(members, title, originIds);
    this.addNewChat(this.state.localChatInstance);
  }

  hasLocal(): boolean {
    return !!this.state.localChatInstance;
  }

  getLocal() {
    return this.state.localChatInstance;
  }

  addNewChat(chat) {
    if (chat.isAssistantType()) {
      return;
    }

    // Refresh memberships from ChatMemberStore
    chat.getMemberships().forEach((membership) => {
      membership.setMember(ChatMemberStore.getMember(membership.getMember().id));
    });

    this.state.chats.add(chat);
    if (chat !== this.state.localChatInstance) {
      this.state.chatsAndSearchedChats.add(chat);
    }
  }

  shouldReloadChats(): boolean {
    return this.state.invalidated;
  }

  markTeacherLeadsInvited(leadIds) {
    leadIds.forEach((leadId) => {
      const member = this.state.members.get(leadId);
      if (member) {
        member.setState('invited');
      }
    });
  }

  handleDispatch(action: Object): void {
    this.waitFor([ChatMemberStore]);

    let chatTarget;

    switch (action.type) {
      case ActionTypes.RESET_STORE:
        this.resetState();
        break;
      case ActionTypes.LIST_CHATS:
        this.state.loadedGroups[action.groupId] = true;
        if (action.chats.length < CHATS_PAGE_SIZE) {
          this.state.allChatsLoadedForGroups[action.groupId] = true;
        }
        action.chats.map((chat) => this.addNewChat(chat));
        this.state.invalidated = false;
        this.emitChange();
        break;
      case ActionTypes.LOAD_CHAT:
        this.addNewChat(action.chat);
        this.emitChange();
        break;
      case ActionTypes.APPEND_CHATS: {
        const groupId = action.groupId;
        const loadedChatsCount = action.chats.length;

        if (loadedChatsCount < CHATS_PAGE_SIZE) {
          this.state.allChatsLoadedForGroups[groupId] = true;
        }

        action.chats.map((chat) => this.addNewChat(chat));
        this.state.chats.sort();
        this.emitChange();
        break;
      }
      case ActionTypes.SEARCH_CHATS:
        action.chats.forEach(chat => this.state.chatsAndSearchedChats.add(chat));
        this.emitChange();
        break;
      case ActionTypes.CREATE_CHAT:
        this.addNewChat(action.chat);
        this.setPosition(action.chat);
        this.emitChange();
        break;
      case ActionTypes.LOAD_LOCAL_CHAT:
        this.loadLocalChat(action.members, action.title, action.originIds);
        this.setPosition(this.state.localChatInstance);
        this.emitChange();
        break;
      case ActionTypes.UPDATE_LOCAL_CHAT:
        this.emitChange();
        break;
      case ActionTypes.DESTROY_LOCAL_CHAT:
        this.clearLocalChat();
        this.emitChange();
        break;
      case ActionTypes.MARK_READ:
      case ActionTypes.READ_MESSAGE:
        this.setAsRead(action.chatId);
        this.emitChange();
        break;
      case ActionTypes.SELECT_CHAT:
        this.state.currentId = action.chatId;
        this.emitChange();
        break;
      case ActionTypes.RESET_SELECTION:
        this.state.currentId = null;
        this.emitChange();
        break;
      case GroupActionTypes.SEND_GROUP_MESSAGE: {
        const message: Message = action.message;
        const groupIds = message.getGroupIds();
        const groups = GroupStore
          .getGroups()
          .filter(group => groupIds.includes(group.id));
        groups.forEach(group => {
          const affectedChats = this.getChats(group).filter(chat => chat.isIndividualType());
          for (const chat of affectedChats) {
            const lastMessage = ChatMessageStore.getChatMessages(chat.id).last();
            const seq = lastMessage ? lastMessage.getSeq() + 1 : 1;
            const chatMessage = ChatMessage.create(
              message.getBody(),
              message.getFiles(),
              chat,
              message.getSender(),
              seq,
              'broadcast',
              group
            );
            chatMessage.set('message', message);
            this.setLastMessageForChat(chat.id, chatMessage);
            if (ChatMessageStore.hasLoadedAnyOf(chat.id)) {
              // If we hadn't loaded any messages yet then the `seq` is likely
              // erroneous. When user navs to the chat messages we'll have to
              // load from the server anyway and the store will get this
              // chatMessage then.
              ChatMessageStore.addMessage(chatMessage);
            }
          }
        });
        this.emitChange();
        break;
      }
      case ActionTypes.SEND_MESSAGE:
        if ((action.message instanceof ChatGapMessage)) {
          console.error('attempt to deliver a gap message');
          break;
        }
        this.setLastMessageForChat(action.chatId, action.message);
        this.emitChange();
        break;
      case ActionTypes.EDIT_MEMBER_FROM_CHAT:
        chatTarget = this.state.chats.get(action.chat.id);
        chatTarget.addToMemberships(action.membership);
        this.emitChange();
        break;
      case ActionTypes.ADD_MEMBERS_TO_CHAT:
        chatTarget = this.state.chats.get(action.chat.id);
        action.memberships.forEach(membership => {
          chatTarget.addToMemberships(membership);
        });
        this.emitChange();
        break;
      case ActionTypes.LOAD_MEMBERS:
        this.state.members.add(action.members.models);
        delete this.state.eligibleMembers; // invalidate cache
        this.state.allMembersLoaded = true;
        this.emitChange();
        break;
      case ActionTypes.UPDATE_CHAT:
        this.state.chats.set(action.chat, { remove: false });
        this.emitChange();
        break;
      case ActionTypes.DEACTIVATE:
        this.deactivate(action.chatId);
        this.emitChange();
        break;
      case RelationshipActionTypes.LOAD_RELATIONSHIP:
      case RelationshipActionTypes.UPDATE_RELATIONSHIP:
        if (action.relationship.getChat()) {
          this.addNewChat(action.relationship.getChat());
          this.emitChange();
        }
        break;
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE:
        action.changes
          .map(change => chatMessageFactory(change.content))
          .filter(message => shouldListenToPushEventFrom(message))
          .forEach(message => this.handleIncomingChatMessage(message));
        break;
      case PushEvents.CHANGESET_UPDATE_CHAT:
        // Refresh all our chats in case we missed a push.
        // The action sends `last_updated_ts` so we only fetch what's new.
        ChatActions.loadMoreChats(rootGroup);
        break;
      case PushEvents.CHANGESET_UPDATE_CHATS:
        ChatActions.resetPromiseMemoization();
        this.resetState();
        break;
      case AppActions.CONNECTION_STATE_CHANGED:
      case PushEvents.CONNECTION_STATE_CHANGED:
        if (action.current !== 'connected') {
          this.state.invalidated = true;
        }
        break;
      case LeadActions.INVITE_LEADS:
        this.markTeacherLeadsInvited(action.leadIds);
        this.emitChange();
        break;
      case ActionTypes.SET_FOCUS_VIEW:
        this.setFocusView(action.focus);
        break;
      default:
        break;
    }
  }
}

export default new ChatStore();



// WEBPACK FOOTER //
// ./src/chat/stores/ChatStore.js